import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { toast } from 'react-toastify';
import { createPrompt, updatePrompt, validatePromptContent } from './llm-prompts.service';

const MAX_CONTENT_LENGTH = 32000;
const MAX_DESCRIPTION_LENGTH = 500;

const emptyForm = {
  promptKey: '',
  promptType: '',
  useCase: '',
  questionType: '',
  difficultyLevel: '',
  promptContent: '',
  description: '',
};

const LLMPromptEditor = ({ open, onClose, onSaved, editPrompt, distinctUseCases = [], distinctPromptTypes = [], distinctDifficulty = [] }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);

  const isEdit = !!editPrompt;

  useEffect(() => {
    if (editPrompt) {
      setForm({
        promptKey: editPrompt.promptKey || '',
        promptType: editPrompt.promptType || '',
        useCase: editPrompt.useCase || '',
        questionType: editPrompt.questionType || '',
        difficultyLevel: editPrompt.difficultyLevel || '',
        promptContent: editPrompt.promptContent || '',
        description: editPrompt.description || '',
      });
    } else {
      setForm(emptyForm);
    }
    setValidationResult(null);
  }, [editPrompt, open]);

  const handleChange = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    if (field === 'promptContent') setValidationResult(null);
  };

  const handleValidate = async () => {
    if (!form.promptContent.trim()) {
      toast.error('Prompt content is empty');
      return;
    }
    setValidating(true);
    try {
      const res = await validatePromptContent(form.promptContent);
      const isValid = res?.result === true;
      setValidationResult(isValid ? 'valid' : 'invalid');
      toast[isValid ? 'success' : 'error'](
        isValid ? 'Prompt content is valid' : (res?.msg || 'Validation failed')
      );
    } catch (e) {
      setValidationResult('invalid');
      toast.error(e || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    if (!form.promptKey.trim() || !form.promptType || !form.useCase || !form.promptContent.trim()) {
      toast.error('Prompt Key, Type, Use Case, and Content are required');
      return;
    }
    if (form.promptContent.length > MAX_CONTENT_LENGTH) {
      toast.error(`Content exceeds ${MAX_CONTENT_LENGTH} characters`);
      return;
    }
    if (form.description.length > MAX_DESCRIPTION_LENGTH) {
      toast.error(`Description exceeds ${MAX_DESCRIPTION_LENGTH} characters`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        questionType: form.questionType || null,
        difficultyLevel: form.difficultyLevel || null,
      };

      if (isEdit) {
        await updatePrompt(editPrompt.promptId, payload);
        toast.success('Prompt updated');
      } else {
        await createPrompt(payload);
        toast.success('Prompt created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e || 'Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  // Extract template variables from content for preview hint
  const templateVars = form.promptContent.match(/\{(\w+)\}/g) || [];
  const uniqueVars = [...new Set(templateVars)];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Prompt' : 'Create Prompt'}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {/* Row 1: Key + Type */}
          <Box display="flex" gap={2}>
            <TextField
              label="Prompt Key"
              value={form.promptKey}
              onChange={handleChange('promptKey')}
              fullWidth
              required
              disabled={isEdit}
              helperText={isEdit ? 'Cannot change key after creation' : 'e.g. clinical_question_generation_system'}
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
            <FormControl fullWidth required>
              <InputLabel>Prompt Type</InputLabel>
              <Select value={form.promptType} label="Prompt Type" onChange={handleChange('promptType')}>
                {distinctPromptTypes.map((t) => (
                  <MenuItem key={t} value={t}>{t.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Row 2: Use Case + Question Type */}
          <Box display="flex" gap={2}>
            <FormControl fullWidth required>
              <InputLabel>Use Case</InputLabel>
              <Select value={form.useCase} label="Use Case" onChange={handleChange('useCase')}>
                {distinctUseCases.map((u) => (
                  <MenuItem key={u} value={u}>{u.replace(/_/g, ' ')}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Question Type (optional)"
              value={form.questionType}
              onChange={handleChange('questionType')}
              fullWidth
              helperText="e.g. clinical_case, diagnostic_reasoning"
            />
          </Box>

          {/* Row 3: Difficulty + Description */}
          <Box display="flex" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Difficulty Level</InputLabel>
              <Select value={form.difficultyLevel} label="Difficulty Level" onChange={handleChange('difficultyLevel')}>
                <MenuItem value="">None</MenuItem>
                {distinctDifficulty.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={form.description}
              onChange={handleChange('description')}
              fullWidth
              helperText={`${form.description.length}/${MAX_DESCRIPTION_LENGTH}`}
            />
          </Box>

          {/* Prompt Content */}
          <TextField
            label="Prompt Content"
            value={form.promptContent}
            onChange={handleChange('promptContent')}
            multiline
            minRows={10}
            maxRows={24}
            fullWidth
            required
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 13 } }}
            helperText={`${form.promptContent.length.toLocaleString()} / ${MAX_CONTENT_LENGTH.toLocaleString()} characters`}
            error={form.promptContent.length > MAX_CONTENT_LENGTH}
          />

          {/* Template variable hint */}
          {form.promptType === 'TEMPLATE' && uniqueVars.length > 0 && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Template variables detected: {uniqueVars.map((v) => (
                <code key={v} style={{ marginRight: 6, background: '#eef2ff', padding: '1px 4px', borderRadius: 3 }}>{v}</code>
              ))}
            </Alert>
          )}

          {/* Validation result */}
          {validationResult === 'valid' && (
            <Alert severity="success" sx={{ py: 0.5 }}>Content passed validation checks</Alert>
          )}
          {validationResult === 'invalid' && (
            <Alert severity="error" sx={{ py: 0.5 }}>Content failed validation — check for unsafe patterns or unbalanced braces</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleValidate} disabled={validating || !form.promptContent.trim()}>
          {validating ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          Validate
        </Button>
        <Box flex={1} />
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
          {isEdit ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LLMPromptEditor;
